/**
 * Member Schedule Page - Olympic Fit Wellness
 * Uses FullCalendar for session browsing and booking
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Calendar, 
  Clock, 
  List,
  Grid,
  Filter,
  ChevronRight
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import SessionCalendar from '../../components/calendar/SessionCalendar';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import api from '../../lib/api';

export default function SchedulePage() {
  const { profile } = useAuth();
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' or 'list'
  const [specializations, setSpecializations] = useState([]);
  const [selectedSpecialization, setSelectedSpecialization] = useState('all');
  const [bookableStaff, setBookableStaff] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState('all');
  const [allowance, setAllowance] = useState(null);

  useEffect(() => {
    const loadFilters = async () => {
      try {
        const [specs, staff, userAllowance] = await Promise.all([
          api.getSpecializations(),
          api.getBookableStaff(),
          api.getMyAllowance()
        ]);
        setSpecializations(specs);
        setBookableStaff(staff);
        setAllowance(userAllowance);
      } catch (error) {
        console.error('Failed to load filters:', error);
      }
    };
    loadFilters();
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Class Schedule</h1>
          <p className="text-gray-600">Browse and book classes and sessions</p>
        </div>
        
        <div className="flex items-center gap-2">
          <Link to="/portal/bookings">
            <Button variant="outline" className="gap-2">
              <List className="w-4 h-4" />
              My Bookings
              <ChevronRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </div>

      {/* Session Allowance Card */}
      {allowance && allowance.total_allowed > 0 && (
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90">Monthly Session Allowance</p>
              <p className="text-2xl font-bold">
                {allowance.total_allowed - allowance.used} / {allowance.total_allowed}
                <span className="text-sm font-normal ml-2">sessions remaining</span>
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm opacity-90">Used this month</p>
              <p className="text-xl font-semibold">{allowance.used} sessions</p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg border p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Filter by:</span>
          </div>
          
          <Select value={selectedSpecialization} onValueChange={setSelectedSpecialization}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {specializations.map((spec) => (
                <SelectItem key={spec.name} value={spec.name}>
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: spec.color }}
                    />
                    {spec.display_name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={selectedStaff} onValueChange={setSelectedStaff}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All Instructors" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Instructors</SelectItem>
              {bookableStaff.map((staff) => (
                <SelectItem key={staff.id} value={staff.id}>
                  {staff.full_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex-1" />

          {/* View Toggle */}
          <div className="flex items-center border rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode('calendar')}
              className={`px-3 py-2 flex items-center gap-1 text-sm ${
                viewMode === 'calendar' 
                  ? 'bg-blue-50 text-blue-600' 
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Calendar className="w-4 h-4" />
              Calendar
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-2 flex items-center gap-1 text-sm ${
                viewMode === 'list' 
                  ? 'bg-blue-50 text-blue-600' 
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <List className="w-4 h-4" />
              List
            </button>
          </div>
        </div>
      </div>

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <SessionCalendar 
          staffId={selectedStaff !== 'all' ? selectedStaff : null}
          showBooking={true}
        />
      )}

      {/* List View - Sessions grouped by day */}
      {viewMode === 'list' && (
        <div className="bg-white rounded-lg border">
          <div className="p-6 text-center text-gray-500">
            <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p>Switch to calendar view for the best booking experience</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => setViewMode('calendar')}
            >
              View Calendar
            </Button>
          </div>
        </div>
      )}

      {/* Quick Info Cards */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg border p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-3 h-3 rounded bg-blue-500" />
            <h3 className="font-medium">Group Classes</h3>
          </div>
          <p className="text-sm text-gray-600">
            Join group fitness sessions. Book instantly - no approval needed.
          </p>
        </div>
        
        <div className="bg-white rounded-lg border p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-3 h-3 rounded bg-green-500" />
            <h3 className="font-medium">1-on-1 Sessions</h3>
          </div>
          <p className="text-sm text-gray-600">
            Request personal training or consultation. Instructor approval required.
          </p>
        </div>
        
        <div className="bg-white rounded-lg border p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-3 h-3 rounded bg-purple-500" />
            <h3 className="font-medium">Workshops</h3>
          </div>
          <p className="text-sm text-gray-600">
            Special events and educational sessions. Limited spots available.
          </p>
        </div>
      </div>
    </div>
  );
}
